import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { PentaContainer } from '../Penta/PentaContainer';
import { Persona } from '../Persona';
import { PersonaYoni } from '../PersonaYoni';
import { Appearer } from '../UiKit/Appearer';
import { Preloader, Preloader0 } from '../UiKit/kit';
import { fetchHistoryPenta, filterHistoryTokens, getTokenKind, HISTORY_INCLUDE_PENTAS } from '../utilsDesign';

const PENDING = Symbol('pending');

const findInPeople = (list = [], token) => list.find(
  (one) => one?.user?.token === token || one?.avatar?.token === token
);

const resolveHistoryToken = (token, context) => {
  const kind = getTokenKind(token);
  if (!kind) return Promise.resolve(null);

  if (kind === 'user' || kind === 'avatar') {
    const fromAvatars = findInPeople(context.avatars, token);
    if (fromAvatars) {
      return Promise.resolve({ kind, user: fromAvatars.user, avatar: fromAvatars.avatar });
    }

    const fromFollowings = findInPeople(context.followings, token);
    if (fromFollowings) {
      return Promise.resolve({ kind, user: fromFollowings.user, avatar: fromFollowings.avatar });
    }

    const fromFollowers = findInPeople(context.followers, token);
    if (fromFollowers) {
      return Promise.resolve({ kind, user: fromFollowers.user, avatar: fromFollowers.avatar });
    }

    const cached = context.cachedUsers?.[token];
    if (cached?.avatar) {
      return Promise.resolve({ kind, user: cached.user, avatar: cached.avatar });
    }
  }

  if (kind === 'user') {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), 10000);
      try {
        context.x.fetchUser(token, (userData) => {
          clearTimeout(timer);
          if (userData?.avatar) {
            finish({ kind, user: userData.user, avatar: userData.avatar });
          } else {
            finish(null);
          }
        });
      } catch {
        clearTimeout(timer);
        finish(null);
      }
    });
  }

  if (kind === 'avatar') {
    return context.x.getAvatar(token)
      .then((avatar) => (avatar ? { kind, user: null, avatar } : null))
      .catch(() => null);
  }

  if (kind === 'penta') {
    if (!HISTORY_INCLUDE_PENTAS) return Promise.resolve(null);
    if (!context.pentas) {
      return Promise.resolve(PENDING);
    }
    const fromList = context.pentas.find((one) => one.token === token);
    if (fromList) {
      return Promise.resolve({ kind, penta: fromList });
    }
    return fetchHistoryPenta(token).then((penta) => (
      penta ? { kind, penta } : null
    ));
  }

  return Promise.resolve(null);
};

export function ListHistory({ mode = 'hd' }) {
  const lang = useContext(LangContext);
  const state = useContext(StateContext);
  const social = useContext(SocialContext);
  const $ui = lang.$ui;
  const tokens = filterHistoryTokens(state.state.showHistory);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const resolveIdRef = useRef(0);
  const panelExpanded = state.state.peoplePanel;

  const tokensKey = tokens?.join('|') ?? '';
  const pentasKey = useMemo(
    () => HISTORY_INCLUDE_PENTAS
      ? (social.pentas?.map((one) => one.token).join('|') ?? '')
      : '',
    [social.pentas]
  );

  useEffect(() => {
    if (!tokensKey) {
      setItems([]);
      setLoading(false);
      return;
    }

    const resolveId = ++resolveIdRef.current;
    setLoading(true);

    const context = { ...social, state: state.state };

    Promise.all(tokens.map((token) => resolveHistoryToken(token, context)))
      .then((resolved) => {
        if (resolveId !== resolveIdRef.current) return;

        const hasPending = resolved.some((item) => item === PENDING);
        const validTokens = [];
        const validItems = [];

        resolved.forEach((item, index) => {
          if (item === PENDING || item) {
            validTokens.push(tokens[index]);
          }
          if (item && item !== PENDING) {
            validItems.push({ ...item, token: tokens[index] });
          }
        });

        const validKey = validTokens.join('|');
        if (!hasPending && validKey !== tokensKey) {
          state.setState.setShowHistory(validTokens);
        }

        setItems(validItems);
        setLoading(hasPending && !validItems.length);
      })
      .catch(() => {
        if (resolveId !== resolveIdRef.current) return;
        setItems([]);
        setLoading(false);
      });
  }, [tokensKey, pentasKey]);

  if (loading && !items.length) {
    return (
      <Preloader0 className='x_choose_preloader0' height='50vh' width='100%'>
        <Preloader width={80} height={80} />
        {$ui('loading')}
      </Preloader0>
    );
  }

  if (!items.length) return null;

  return (
    <div className={`x_choose ${panelExpanded ? 'x_choose--v x_choose--history' : ''}`}>
      {items.map((item, ind) => (
        <Appearer key={`history_${item.token}`} order={ind}>
          {item.kind === 'penta' ? (
            <PentaContainer
              peoplePanel={panelExpanded}
              penta={item.penta}
              dropdownActions={['deleteFromHistory']}
              mode={mode}
            />
          ) : mode === 'hd' ? (
            <Persona
              mode={mode}
              listId='history'
              actions={['share', 'deleteFromHistory']}
              panelExpanded={panelExpanded}
              avatar={item.avatar}
              user={item.user}
              isMe={item.avatar?.isMe}
              addClassName={panelExpanded ? 'mypeople' : 'people'}
            />
          ) : (
            <PersonaYoni
              mode={mode}
              listId='history'
              actions={['share', 'deleteFromHistory']}
              panelExpanded={panelExpanded}
              avatar={item.avatar}
              user={item.user}
              isMe={item.avatar?.isMe}
              addClassName={panelExpanded ? 'mypeople' : 'people'}
            />
          )}
        </Appearer>
      ))}
    </div>
  );
}
